// Match lifecycle, extracted from GameBoard.jsx: session init + join
// retry, the first-player dice roll-off (broadcast + subscribe +
// resolve, with durable roll state for resume), deck spawning into
// player slots, match start, manual/derived match end, and board
// clearing. The roll-off resolver chains into the Harbinger ceremony
// and opening draw, which live in their own controllers.
//
// Host contract: connection/sync, state (matchPhase, rollState,
// playerSlots, gameState), deck loading APIs, spawnDeck recipe,
// mesh/pile teardown primitives, logAction, autoSave.

import { UI } from '../../../utils/arena/uiSounds';
import { createPileMesh } from '../../../utils/game/cardMesh';
import { EffectLedger } from '../../../utils/game/engine/automation';
import { createGameState, spawnDeck, spawnOpponentCountStacks } from '../../../utils/game/gameState';
import { MatchConnection } from '../../../utils/game/net/MatchConnection';
import { resolveSeat } from '../../../utils/game/net/seatPerspective';
import { createRoomWithCode, disconnectSocket, joinRoom, spectateRoom } from '../../../utils/game/socketClient';
import { getSpawnPoint } from '../../../utils/game/spawnConfig';
import { isAuthMatchEnabled } from '../../../utils/game/net/authMatchFlag';
import { scrubUnknownBoardMeshesFromMirror } from './authZones';
import * as harbingerCeremony from '../controllers/harbingerCeremony';
import { toast } from 'sonner';
import { t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

export async function initSession(host) {
  const { sessionMode } = host.props;
  if (!host.conn) {
    host.conn = new MatchConnection({
      transport: { createRoomWithCode, joinRoom, spectateRoom, disconnectSocket },
      host: host._makeConnectionHost(),
      getProps: () => host.props,
    });
  }

  // Connection kinds (host / guest / spectate / resume) are owned by
  // MatchConnection. The app is fully online. Every mount, including
  // server-backed solo and bot matches, is a real server session. The
  // 'solo' kind survives only as a defensive fallback for a malformed
  // mount and connects nothing.
  if (resolveSeat({ sessionMode, props: host.props }).kind !== 'solo') {
    return host.conn.start();
  }
}

export function cancelJoinAttempts(host) {
  host.conn?.abortJoin();
  disconnectSocket();
  host.setState({ canCancelJoin: false });
  host.props.onExit?.();
}

export function requestExit(host) {
  host.closeAllMenus();
  host.setState({ showExitConfirm: true });
}

// Who is allowed to end the match. The seat eligibility only; the caller
// still decides enabled vs "waiting for opponent" from connectionStatus.
//
// The spectate seat satisfies every other clause: handleDraftSpectate sets
// isArenaMatch with a null instanceVisibility and _startSpectate sets
// connected, so isSpectating is the only thing separating a viewer from a
// participant here. _endMatchManually has no spectator branch and would mount
// ArenaMatchResult, which auto-submits a report for a match this seat never
// played. Shared by the menu item and _clickEndMatch so the two cannot drift.
export function canEndMatch(host) {
  return !host.props.isSpectating
    && !!host.props.isArenaMatch
    && host.props.instanceVisibility !== 'solo';
}

export function subscribeToFirstPlayerRoll(host, handler) {
  host._firstPlayerRollSubscriber = handler;
  if (host._pendingFirstPlayerRolls.length) {
    const queued = host._pendingFirstPlayerRolls;
    host._pendingFirstPlayerRolls = [];
    for (const msg of queued) handler(msg);
  }
  return () => {
    if (host._firstPlayerRollSubscriber === handler) {
      host._firstPlayerRollSubscriber = null;
    }
  };
}

export function subscribeToFirstPlayerChoice(host, handler) {
  host._firstPlayerChoiceSubscriber = handler;
  if (host._pendingFirstPlayerChoice) {
    const msg = host._pendingFirstPlayerChoice;
    host._pendingFirstPlayerChoice = null;
    handler(msg);
  }
  return () => {
    if (host._firstPlayerChoiceSubscriber === handler) {
      host._firstPlayerChoiceSubscriber = null;
    }
  };
}

// Deliver newly-discovered initiative fields through the same
// subscriber paths the modal uses for live broadcasts. If the modal
// is already mounted (subscriber registered), the values stream in
// as if they'd arrived over the wire. The existing animation +
// dedup logic handles them. If the modal hasn't subscribed yet, the
// values queue up and replay on subscribe.
export function replayInitiativeFields(host, mergedRollState, previousRollState) {
  if (!mergedRollState) return;
  const prev = previousRollState || {};
  const wasUnknownNum = (key) => typeof prev[key] !== 'number';
  const wasUnknownSeat = (key) => prev[key] !== 1 && prev[key] !== 2;

  // KNOWN → NULL is the server's TIE reset: the reducer clears BOTH rolls so
  // the seats re-roll. Deliver it as a reset message so the modal clears the
  // die and re-arms. Without this the modal keeps a stale value forever (the
  // die goes dead for one seat, and the roll-off can later "resolve" against
  // a roll the server already discarded).
  const deliverRoll = (msg) => {
    if (host._firstPlayerRollSubscriber) host._firstPlayerRollSubscriber(msg);
    else host._pendingFirstPlayerRolls.push(msg);
  };
  let resetFired = false;
  if (!wasUnknownNum('hostRoll') && typeof mergedRollState.hostRoll !== 'number') {
    deliverRoll({ seat: 'host', reset: true });
    resetFired = true;
  }
  if (!wasUnknownNum('guestRoll') && typeof mergedRollState.guestRoll !== 'number') {
    deliverRoll({ seat: 'guest', reset: true });
    resetFired = true;
  }
  if (resetFired) toast.message(t('Tied roll. Roll again.'));

  if (typeof mergedRollState.hostRoll === 'number' && wasUnknownNum('hostRoll')) {
    const msg = { seat: 'host', value: mergedRollState.hostRoll };
    if (host._firstPlayerRollSubscriber) host._firstPlayerRollSubscriber(msg);
    else host._pendingFirstPlayerRolls.push(msg);
  }
  if (typeof mergedRollState.guestRoll === 'number' && wasUnknownNum('guestRoll')) {
    const msg = { seat: 'guest', value: mergedRollState.guestRoll };
    if (host._firstPlayerRollSubscriber) host._firstPlayerRollSubscriber(msg);
    else host._pendingFirstPlayerRolls.push(msg);
  }
  if ((mergedRollState.firstPlayerSeat === 1 || mergedRollState.firstPlayerSeat === 2)
      && wasUnknownSeat('firstPlayerSeat')) {
    const msg = { firstPlayerSeat: mergedRollState.firstPlayerSeat };
    if (host._firstPlayerChoiceSubscriber) host._firstPlayerChoiceSubscriber(msg);
    else host._pendingFirstPlayerChoice = msg;
  }
}

// --- Deck Spawning ---
// Stash the opponent's deck and notify the asset gate. The gate
// runs phase 2 (downloads + decode) against this deck and flips
// props.assetsReady true when done. At which point
// componentDidUpdate calls _spawnQueuedOpponent and the cards
// finally hit the table. Spawning before the gate finishes would
// race the texture loader against the download, producing the same
// transparent-card glitch the gate is meant to prevent.
export function queueOpponentSpawn(host, deck, playerNum) {
  host._pendingOpponentSpawn = { deck, playerNum };
  host.props.onOpponentDeckResolved?.(deck);
  host._spawnQueuedOpponent();
}

export function spawnQueuedOpponent(host) {
  if (!host._pendingOpponentSpawn) return;
  if (host.props.assetsReady === false) return;
  const { deck, playerNum } = host._pendingOpponentSpawn;
  host._pendingOpponentSpawn = null;
  try {
    host.sync.withSuppressed(() => {
      host.spawnSelectedDeck(deck, playerNum);
    });
  } catch (err) {
    console.error('Failed to spawn opponent deck:', err);
  }
  // Solo resume: this queued spawn is the SECOND seat's fresh deck and can
  // land AFTER the durable Full already folded. Re-scrub so its avatar
  // (an id the mirror doesn't know) can't linger as a stacked phantom next
  // to the server-restored one. No-op on a fresh boot (ids match the room).
  if (host.props.isCasualSandbox && isAuthMatchEnabled()) {
    const m = host._authMirror?.getState?.();
    if (m?.cards && Object.keys(m.cards).length) scrubUnknownBoardMeshesFromMirror(host, m);
  }
}

export function spawnSelectedDeck(host, deck, playerNum = 1) {
  const { sorceryCards } = host.props;
  const { spawnConfig } = host.state;
  const suffix = playerNum === 1 ? '' : '2';
  const points = {
    spellbook: getSpawnPoint(spawnConfig, `spellbook${suffix}`),
    atlas: getSpawnPoint(spawnConfig, `atlas${suffix}`),
    avatar: getSpawnPoint(spawnConfig, `avatar${suffix}`),
    collection: getSpawnPoint(spawnConfig, `collection${suffix}`),
  };
  const isP2 = playerNum === 2;
  const result = spawnDeck(deck, sorceryCards, points, isP2);

  host.setState((state) => {
    const gameState = { ...state.gameState };
    gameState.piles = [...gameState.piles, ...result.piles];
    const spawnedDecks = { ...state.spawnedDecks, [playerNum]: deck };

    if (result.avatarCard) {
      // Solo boot: the up-front mirror Full can rebuild this avatar's mesh
      // (position-less, parked at world origin) BEFORE the asset-gated deck
      // spawn runs. AddCardToTable then skips the duplicate mesh and its
      // position emit never fires, leaving the avatar with no authoritative
      // cell. Seed the spawn-point position explicitly in that case so the
      // server records it and the position fold snaps the mesh into place.
      const meshExisted = host.meshes.has(result.avatarCard.id);
      host.addCardToTable(result.avatarCard);
      if (meshExisted && host.props.isCasualSandbox) {
        host._emitPosition?.(result.avatarCard.id, result.avatarCard.x, result.avatarCard.z);
      }
    }

    return { gameState, spawnedDecks, showDeckPicker: false };
  });

  for (const pile of result.piles) {
    const mesh = createPileMesh(pile);
    if (mesh) {
      host.scene.scene.add(mesh);
      host.pileMeshes.set(pile.id, mesh);
    }
  }

  host.createTrackerTokens();
  host.createTrackerButtons();

  // Broadcast deck spawn to the other player (legacy path. Server-prepared
  // matches use room:joined so this only fires for casual/invite decks).
  host._broadcastDeckSpawn(deck, playerNum);

  // In casual sandbox, opening draws don't fire on spawn. They fire
  // when the user clicks "Start Game" (both sides simultaneously).
  // This lets the user lay down both decks before committing to a
  // match, and spawn-to-replace during a game naturally triggers
  // endGame() to reset trackers without drawing a fresh hand yet.
}

// R2b. Spawn the OPPONENT's draw piles from COUNTS ONLY (true hidden info).
// The server withholds the opponent's deck contents on room:joined and sends
// `opponentDeckCounts` instead; we render the opponent's Spellbook/Atlas as
// correctly-sized face-down stacks of identity-free placeholders. This is the
// auth-mode replacement for `spawnSelectedDeck(opponentDeck, playerNum)`. Same
// pile-mesh output, but no opponent card identity ever enters client memory.
// The opponent's AVATAR is the exception: it is public in Sorcery (both players
// see each other's), so the server ships it in the withheld payload and we spawn
// just the avatar mesh on the board. No trackers / opening-draw / broadcast
// (those are owner-side only).
export function spawnOpponentCounts(host, counts, playerNum = 2, avatarCard = null) {
  const { spawnConfig } = host.state;
  const suffix = playerNum === 1 ? '' : '2';
  const points = {
    spellbook: getSpawnPoint(spawnConfig, `spellbook${suffix}`),
    atlas: getSpawnPoint(spawnConfig, `atlas${suffix}`),
  };
  const { piles } = spawnOpponentCountStacks(counts, points, playerNum === 2);

  host.setState((state) => ({
    gameState: { ...state.gameState, piles: [...state.gameState.piles, ...piles] },
  }));

  for (const pile of piles) {
    const mesh = createPileMesh(pile);
    if (mesh) {
      host.scene.scene.add(mesh);
      host.pileMeshes.set(pile.id, mesh);
    }
  }

  // Spawn the opponent's public avatar onto the board. Reuse spawnDeck's avatar
  // resolution + positioning for a single-card deck and take only its avatar
  // (the empty pile it returns is discarded. The count stacks above own the
  // opponent piles). Suppressed so the spawn never echoes a card:place back.
  if (avatarCard) {
    try {
      const avPoint = getSpawnPoint(spawnConfig, `avatar${suffix}`);
      const { avatarCard: oppAvatar } = spawnDeck(
        { cards: [avatarCard] }, host.props.sorceryCards, { avatar: avPoint }, playerNum === 2,
      );
      if (oppAvatar) host.sync.withSuppressed(() => host.addCardToTable(oppAvatar));
    } catch (err) {
      console.error('Failed to spawn opponent avatar:', err);
    }
  }
}

export async function loadAndSpawnDeck(host, deckId, playerNum = 1) {
  // Legacy replace-prompt behaviour. Any existing content triggers
  // the confirmation dialog.
  const hasExistingContent = host.meshes.size > 0 || host.state.gameState.piles.length > 0 || host.state.handCards.length > 0;
  if (hasExistingContent) {
    host.setState({
      pendingSpawnDeckId: deckId,
      pendingSpawnPlayerNum: playerNum,
      showSpawnConfirm: true,
      showDeckPicker: false,
    });
    return;
  }
  await host.doSpawnDeck(deckId, playerNum);
}

export async function confirmSpawnDeck(host) {
  const { pendingSpawnDeckId, pendingSpawnPlayerNum } = host.state;
  host.setState({ showSpawnConfirm: false });

  // Replace: wipe the board entirely and spawn the new deck as P1
  // (the only hand the local user owns).
  host.clearBoard();
  await host.doSpawnDeck(pendingSpawnDeckId, pendingSpawnPlayerNum);
}

// First-player dice modal resolved. Pure VIEW bookkeeping. The server owns
// the whole start choreography off SET_FIRST_PLAYER: currentTurn/turnNumbers
// commit in the reducer (folded here by applyTurnPhaseFromMirror), the
// pre-game ceremonies (Dragonlord set-aside, Harbinger divination) are staged
// as server pendingChoice prompts, and the start director auto-advances
// preGame → openingDraw → deal. This resolver only closes the modal, persists
// the roll display state, and logs the outcome. The roll *winner* may have
// declined the initiative, so the log keys off firstPlayerSeat, not the raw
// winner.
export function resolveFirstPlayerRoll(host, { winnerSeat, firstPlayerSeat }) {
  // Reconnect replays the known roll-off; mid-match the start choreography is
  // long over. Bail while the mirror hasn't landed (a resolve racing the Full)
  // or once the start is complete.
  const mirrorPhase = host._authMirror?.getState?.()?.phase;
  if (!mirrorPhase || mirrorPhase.startStep === 'done') return;
  const firstSeat = firstPlayerSeat || winnerSeat;
  const isSandbox = !!host.props.isCasualSandbox;

  // Persist the resolved winner/firstPlayer into the roll display state so a
  // save fired around the exit still resumes with the modal closed.
  const prevRollState = host.state.firstPlayerRollState || {};
  host.setState({
    showFirstPlayerDice: false,
    firstPlayerRollState: {
      hostRoll: prevRollState.hostRoll ?? null,
      guestRoll: prevRollState.guestRoll ?? null,
      winnerSeat: winnerSeat ?? null,
      firstPlayerSeat: firstSeat ?? null,
    },
  }, () => host.autoSave({ force: true }).catch(() => {}));

  host._diceModalPending = false;
  host._scheduleBoardTutorialCheck(600);

  // Use absolute names so both clients render the same line. Both sides
  // resolve from the same server rolls, so this needs no broadcast.
  const labelFor = (seat) => isSandbox
    ? msg('Seat {n}', { n: seat })
    : host._displayNameForPlayer(seat === 1 ? 'p1' : 'p2');
  const declined = firstPlayerSeat != null && firstPlayerSeat !== winnerSeat;
  const line = declined
    ? msg('{winner} won the roll. {first} goes first', { winner: labelFor(winnerSeat), first: labelFor(firstSeat) })
    : msg('{winner} won the roll. Going first', { winner: labelFor(winnerSeat) });
  host.addLogEntry(line, 'system', 'system');
}

// Modal → parent bridge: every time the dice state advances (a seat
// rolls, a tie clears, the winner is decided, the first player is
// picked), the modal calls this so we can force-save. Without the
// force, a sudden app close between roll and the 60s autosave tick
// would lose the roll. The peer might also have missed the live
// broadcast. Having the value on disk is the floor that everything
// else (resume, reconnect handshake) can build on.
export function onFirstPlayerRollStateChange(host, rollState) {
  host.setState(
    { firstPlayerRollState: rollState || host.state.firstPlayerRollState },
    () => host.autoSave({ force: true }).catch(() => {}),
  );
}

export function clearBoard(host) {
  // Remove all table cards
  for (const [, mesh] of host.meshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
  }
  host.meshes.clear();
  for (const cardId of Array.from(host.cardEffects.keys())) {
    host._removeAllCardEffects(cardId);
  }

  // Remove all pile meshes
  for (const [, mesh] of host.pileMeshes) {
    host.scene.scene.remove(mesh);
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
  }
  host.pileMeshes.clear();

  // Clear state
  host.setState({
    gameState: createGameState(host.props.startingHp || 20),
    handCards: [],
    gameLog: [],
    harbingerCeremony: null,
    harbingerSweep: null,
  });
  // v1.111. Full-board reset wipes the ledger and any in-flight
  // Harbinger ceremony state. Without this the previous match's
  // marks would persist into the fresh board.
  host.effectLedger = new EffectLedger();
  host._pendingHarbingerInstall = null;
  host._harbingerSweepCancel?.();
  host._harbingerSweepCancel = null;
}

// Resolve a deckId to a full deck object. Prop-provided decks (arena
// inline) win over API-loaded saved decks. Returns null if neither
// source knows about the id. Shared by doSpawnDeck and the casual
// mid-game respawn path so both load decks identically.
export async function resolveDeck(host, deckId) {
  try {
    const propDeck = (host.props.savedDecks || []).find((d) => d.id === deckId);
    if (propDeck?.cards?.length > 0) return propDeck;
    const { loadSavedDeckById } = await import('../../../utils/deckStorageApi');
    return await loadSavedDeckById(deckId, 'sorcery');
  } catch (error) {
    console.error('Failed to load deck for game:', error);
    toast.error(t('Failed to spawn deck'));
    return null;
  }
}

export async function doSpawnDeck(host, deckId, playerNum) {
  const deck = await host._resolveDeck(deckId);
  if (!deck) return;
  host.spawnSelectedDeck(deck, playerNum);
  toast.success(t('Spawned "{name}" as P{n}', { name: deck.name, n: playerNum }));

}
